import { useEffect, useRef, useState } from "react";
import { BRANCH_TYPES, changeType, CHOICE_TYPES, GRID_TYPES, MAX_GRID_COLUMNS, MAX_GRID_ROWS, MAX_OPTIONS, newId, OTHER_TYPES, QUESTION_TYPES, SUBMIT } from "../surveyQuestions";

const ICON_BUTTON = "flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-[13px] font-semibold text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-30";
const FIELD = "w-full rounded-xl bg-gray-50 px-3.5 py-2.5 text-[15px] text-gray-900 outline-none ring-1 ring-transparent transition placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-gray-900";
const PLACEHOLDER_ANSWER = "rounded-xl border border-dashed border-gray-200 px-3.5 py-2.5 text-[14px] text-gray-400";
const SMALL_SELECT = "rounded-lg bg-white px-2.5 py-1.5 text-[13px] font-semibold text-gray-700 ring-1 ring-gray-200 outline-none focus:ring-2 focus:ring-gray-900";
const SWITCH = "relative h-6 w-10 shrink-0 rounded-full bg-gray-200 transition peer-checked:bg-gray-900 peer-focus-visible:ring-2 peer-focus-visible:ring-gray-900 peer-focus-visible:ring-offset-2 after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition peer-checked:after:translate-x-4";

const VALIDATION_KINDS = {
    SHORT_TEXT: [{ value: '', label: '형식 검사 안 함' }, { value: 'EMAIL', label: '이메일' }, { value: 'NUMBER', label: '숫자' }, { value: 'LENGTH', label: '글자 수' }],
    LONG_TEXT: [{ value: '', label: '형식 검사 안 함' }, { value: 'LENGTH', label: '글자 수' }],
};

/** 이동할 곳 고르기: 다음 섹션(빈 값) / 뒤쪽 섹션 / 제출. 지금 값이 목록에 없으면(섹션을 옮겼거나 지움) 다음 섹션으로 보인다 */
function TargetSelect({ value, targets, onChange, nextLabel = '다음 섹션으로', label }) {
    const known = value === SUBMIT || targets.some(t => t.id === value);
    return (
        <select value={known ? value : ''} onChange={(e) => onChange(e.target.value || undefined)} aria-label={label} className={`${SMALL_SELECT} max-w-[200px] truncate`}>
            <option value="">{nextLabel}</option>
            {targets.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            <option value={SUBMIT}>제출하기</option>
        </select>
    );
}

/**
 * 선택지·행·열 목록 편집. Enter = 다음 칸, 빈 칸에서 Backspace = 빼기, 여러 줄 붙여 넣기 = 줄마다 하나.
 * targets 가 있으면 칸마다 이동할 곳(섹션 이동), onRemoveOther 가 있으면 끝에 "기타" 줄.
 */
function OptionList({ items, onChange, max, noun, marker, targets, onRemoveOther }) {
    const refs = useRef({});
    const [focusId, setFocusId] = useState(null);

    // 칸을 새로 넣으면 그 칸으로 커서
    useEffect(() => {
        if (focusId) {
            refs.current[focusId]?.focus();
            setFocusId(null);
        }
    }, [focusId]);

    const setItem = (id, fields) => onChange(items.map(o => (o.id === id ? { ...o, ...fields } : o)));
    const insert = (afterIndex, labels) => {
        const added = labels.slice(0, max - items.length).map(label => ({ id: newId(), label }));
        if (!added.length) {
            return;
        }
        const next = [...items];
        next.splice(afterIndex + 1, 0, ...added);
        onChange(next);
        setFocusId(added[added.length - 1].id);
    }
    const remove = (id) => onChange(items.filter(o => o.id !== id));

    // 여러 줄을 붙여 넣으면 줄마다 하나 (엑셀·메모에서 목록 옮기기)
    const handlePaste = (e, index, item) => {
        const lines = e.clipboardData.getData('text/plain').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        if (lines.length < 2) {
            return;
        }
        e.preventDefault();
        const [first, ...rest] = lines;
        const next = items.map(o => (o.id === item.id ? { ...o, label: (item.label + first).slice(0, 200) } : o));
        next.splice(index + 1, 0, ...rest.slice(0, max - next.length).map(label => ({ id: newId(), label: label.slice(0, 200) })));
        onChange(next);
    }

    return (
        <div className="flex flex-col gap-1.5">
            {items.map((item, index) => (
                <div key={item.id} className="group flex items-center gap-2.5">
                    {marker(index)}
                    <input
                        ref={(el) => { refs.current[item.id] = el; }}
                        value={item.label}
                        onChange={(e) => setItem(item.id, { label: e.target.value })}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                e.preventDefault();
                                insert(index, ['']);
                            }
                            if (e.key === 'Backspace' && !item.label && items.length > 1) {
                                e.preventDefault();
                                remove(item.id);
                                setFocusId(items[index > 0 ? index - 1 : 1].id);
                            }
                        }}
                        onPaste={(e) => handlePaste(e, index, item)}
                        maxLength={200}
                        placeholder={`${noun} ${index + 1}`}
                        aria-label={`${noun} ${index + 1}`}
                        className="min-w-0 flex-1 border-0 border-b border-transparent bg-transparent py-1.5 text-[15px] text-gray-800 outline-none transition placeholder:text-gray-300 hover:border-gray-200 focus:border-gray-900"
                    />
                    {targets && <TargetSelect value={item.goTo} targets={targets} onChange={(goTo) => setItem(item.id, { goTo })} label={`${noun} ${index + 1} 다음에 갈 곳`} />}
                    {items.length > 1 &&
                        <button type="button" onClick={() => remove(item.id)} aria-label={`${noun} ${index + 1} 빼기`} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-60 transition hover:bg-gray-100 hover:text-gray-900 group-hover:opacity-100">✕</button>
                    }
                </div>
            ))}
            {onRemoveOther &&
                <div className="group flex items-center gap-2.5">
                    {marker(items.length)}
                    <span className="flex-1 border-b border-dashed border-gray-200 py-1.5 text-[15px] text-gray-400">기타: 응답자가 직접 입력</span>
                    <button type="button" onClick={onRemoveOther} aria-label="기타 빼기" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-60 transition hover:bg-gray-100 hover:text-gray-900 group-hover:opacity-100">✕</button>
                </div>
            }
            {items.length < max &&
                <button type="button" onClick={() => insert(items.length - 1, [''])} className="ml-7 self-start rounded-lg px-1 py-1 text-[13px] font-semibold text-gray-500 hover:text-gray-900">
                    ＋ {noun} 추가 <span className="font-normal text-gray-400">· Enter 로 다음 칸, 여러 줄 붙여 넣기 가능</span>
                </button>
            }
        </div>
    );
}

const choiceMarker = (type) => (index) => (
    <span className={`flex h-5 w-5 shrink-0 items-center justify-center border-2 border-gray-300 text-[10px] font-bold text-gray-400 ${type === 'SINGLE_CHOICE' ? 'rounded-full' : type === 'MULTIPLE_CHOICE' ? 'rounded-md' : 'border-0'}`} aria-hidden="true">
        {type === 'DROPDOWN' ? index + 1 : ''}
    </span>
);
const gridMarker = (index) => <span className="w-5 shrink-0 text-center text-[12px] font-bold text-gray-400" aria-hidden="true">{index + 1}</span>;

/** 범위 칸 (형식 검사 최소·최대) */
function RangeInput({ value, onChange, label, integer }) {
    return (
        <input
            type="number"
            value={value ?? ''}
            step={integer ? 1 : 'any'}
            min={integer ? 0 : undefined}
            onChange={(e) => onChange(e.target.value === '' ? undefined : integer ? Math.max(0, Math.floor(Number(e.target.value))) : Number(e.target.value))}
            placeholder={label}
            aria-label={label}
            className="w-24 rounded-lg bg-white px-2.5 py-1.5 text-[13px] ring-1 ring-gray-200 outline-none focus:ring-2 focus:ring-gray-900"
        />
    );
}

/**
 * 질문 하나 편집 카드. 선택된(active) 카드는 왼쪽에 색 막대.
 * targets: 이 질문에서 갈 수 있는 뒤쪽 섹션 (섹션 이동·섹션 다음), sectionNumber: PAGE 카드의 섹션 번호
 */
export function FgQuestionCard({ question, index, count, active, targets, hasSections, sectionNumber, sectionCount, onFocus, onChange, onMove, onDuplicate, onRemove }) {
    const patch = (fields) => onChange({ ...question, ...fields });
    const isChoice = CHOICE_TYPES.includes(question.type);
    const isGrid = GRID_TYPES.includes(question.type);
    const isPage = question.type === 'PAGE';
    const isBlock = question.type === 'SECTION' || isPage;
    const validation = question.validation || {};
    const setValidation = (fields) => {
        const next = { ...validation, ...fields };
        patch({ validation: next.kind ? next : undefined });
    }

    return (
        <div
            data-survey-question={question.id}
            onFocusCapture={onFocus}
            onMouseDown={onFocus}
            className={`relative scroll-mt-24 overflow-hidden rounded-2xl bg-white ring-1 transition-shadow data-[flash]:ring-2 data-[flash]:ring-[var(--mainColor)] ${isPage ? 'mt-4' : ''} ${active ? 'shadow-[0_6px_24px_rgba(0,0,0,0.07)] ring-gray-200' : 'ring-gray-100'}`}
        >
            {isPage &&
                <div className="flex items-center gap-2 bg-gray-900 px-5 py-2 text-[12px] font-bold text-white min-[993px]:px-6">
                    <span>섹션 {sectionNumber}</span><span className="text-white/40">/ {sectionCount}</span>
                    <span className="ml-auto font-medium text-white/60">응답자는 여기부터 다음 화면에서 봐요</span>
                </div>
            }
            <span className={`absolute inset-y-0 left-0 w-1 transition-colors ${active ? 'bg-[var(--mainColor)]' : 'bg-transparent'}`} aria-hidden="true" />

            <div className="flex flex-col gap-3 p-4 pl-5 min-[993px]:p-5 min-[993px]:pl-6">
                <div className="flex flex-col gap-2 min-[600px]:flex-row">
                    <input
                        data-question-title
                        value={question.title}
                        onChange={(e) => patch({ title: e.target.value })}
                        maxLength={300}
                        placeholder={isPage ? '섹션 제목' : isBlock ? '제목' : '질문'}
                        aria-label={`${index + 1}번 질문 제목`}
                        className={`${FIELD} flex-1 font-semibold ${isBlock ? 'text-[17px]' : ''}`}
                    />
                    <div className="relative min-[600px]:w-[160px]">
                        <select
                            value={question.type}
                            onChange={(e) => onChange(changeType(question, e.target.value))}
                            aria-label="질문 유형"
                            className="h-full w-full appearance-none rounded-xl bg-white py-2.5 pl-3.5 pr-8 text-[14px] font-semibold text-gray-700 outline-none ring-1 ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-gray-900"
                        >
                            {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon}  {t.label}</option>)}
                        </select>
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400" aria-hidden="true">▼</span>
                    </div>
                </div>

                <input
                    value={question.description || ''}
                    onChange={(e) => patch({ description: e.target.value })}
                    maxLength={1000}
                    placeholder="설명 (선택)"
                    aria-label="질문 설명"
                    className="w-full border-0 border-b border-transparent bg-transparent px-1 py-1 text-[13px] text-gray-600 outline-none transition placeholder:text-gray-300 hover:border-gray-200 focus:border-gray-900"
                />

                {(question.type === 'SHORT_TEXT' || question.type === 'LONG_TEXT') &&
                    <>
                        <div className={`${PLACEHOLDER_ANSWER} ${question.type === 'SHORT_TEXT' ? 'min-[600px]:w-1/2' : 'h-20'}`}>
                            {validation.kind === 'EMAIL' ? 'name@example.com' : validation.kind === 'NUMBER' ? '숫자' : question.type === 'SHORT_TEXT' ? '짧은 답변' : '긴 답변'}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <select value={validation.kind || ''} onChange={(e) => patch({ validation: e.target.value ? { kind: e.target.value } : undefined })} aria-label="응답 형식 검사" className={SMALL_SELECT}>
                                {VALIDATION_KINDS[question.type].map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                            </select>
                            {(validation.kind === 'NUMBER' || validation.kind === 'LENGTH') &&
                                <>
                                    <RangeInput value={validation.min} onChange={(min) => setValidation({ min })} label={validation.kind === 'LENGTH' ? '최소 글자' : '최솟값'} integer={validation.kind === 'LENGTH'} />
                                    <span className="text-gray-400">~</span>
                                    <RangeInput value={validation.max} onChange={(max) => setValidation({ max })} label={validation.kind === 'LENGTH' ? '최대 글자' : '최댓값'} integer={validation.kind === 'LENGTH'} />
                                    <span className="text-[12px] text-gray-400">비우면 제한 없음</span>
                                </>
                            }
                        </div>
                    </>
                }
                {question.type === 'DATE' && <div className={`${PLACEHOLDER_ANSWER} w-44`}>연도. 월. 일</div>}
                {question.type === 'SECTION' && <p className="rounded-xl bg-gray-50 px-3.5 py-2.5 text-[12px] text-gray-500">답하는 칸 없이 제목과 설명만 보여요. 같은 화면 안에서 질문을 주제별로 나눌 때 써요.</p>}

                {isChoice &&
                    <>
                        <OptionList
                            items={question.options}
                            onChange={(options) => patch({ options })}
                            max={MAX_OPTIONS}
                            noun="선택지"
                            marker={choiceMarker(question.type)}
                            targets={question.branching ? targets : null}
                            onRemoveOther={question.allowOther ? () => patch({ allowOther: undefined }) : null}
                        />
                        {OTHER_TYPES.includes(question.type) && !question.allowOther &&
                            <button type="button" onClick={() => patch({ allowOther: true })} className="ml-7 self-start rounded-lg px-1 py-0.5 text-[13px] font-semibold text-gray-500 hover:text-gray-900">＋ "기타" 추가</button>
                        }
                    </>
                }

                {isGrid &&
                    <div className="grid gap-4 min-[600px]:grid-cols-2">
                        <div>
                            <p className="mb-1.5 text-[12px] font-bold text-gray-500">행 <span className="font-normal text-gray-400">(질문할 항목)</span></p>
                            <OptionList items={question.rows} onChange={(rows) => patch({ rows })} max={MAX_GRID_ROWS} noun="행" marker={gridMarker} />
                        </div>
                        <div>
                            <p className="mb-1.5 text-[12px] font-bold text-gray-500">열 <span className="font-normal text-gray-400">({question.type === 'CHOICE_GRID' ? '행마다 하나 고름' : '행마다 여러 개 고름'})</span></p>
                            <OptionList items={question.options} onChange={(options) => patch({ options })} max={MAX_GRID_COLUMNS} noun="열" marker={choiceMarker(question.type === 'CHOICE_GRID' ? 'SINGLE_CHOICE' : 'MULTIPLE_CHOICE')} />
                        </div>
                    </div>
                }

                {question.type === 'LINEAR_SCALE' &&
                    <div className="flex flex-col gap-2.5">
                        <div className="flex items-center gap-2 text-[14px]">
                            <select value={question.scale.min} onChange={(e) => patch({ scale: { ...question.scale, min: Number(e.target.value) } })} aria-label="배율 시작" className="rounded-lg bg-white px-2.5 py-1.5 font-semibold ring-1 ring-gray-200 outline-none focus:ring-2 focus:ring-gray-900">
                                {[0, 1].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-gray-400">~</span>
                            <select value={question.scale.max} onChange={(e) => patch({ scale: { ...question.scale, max: Number(e.target.value) } })} aria-label="배율 끝" className="rounded-lg bg-white px-2.5 py-1.5 font-semibold ring-1 ring-gray-200 outline-none focus:ring-2 focus:ring-gray-900">
                                {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        <div className="grid gap-2 min-[600px]:grid-cols-2">
                            {[['minLabel', question.scale.min], ['maxLabel', question.scale.max]].map(([key, n]) => (
                                <label key={key} className="flex items-center gap-2">
                                    <span className="w-6 shrink-0 text-center text-[13px] font-bold text-gray-500">{n}</span>
                                    <input value={question.scale[key] || ''} onChange={(e) => patch({ scale: { ...question.scale, [key]: e.target.value } })} maxLength={30} placeholder={key === 'minLabel' ? '이름표 (선택, 예: 전혀 아니다)' : '이름표 (선택, 예: 매우 그렇다)'} className={`${FIELD} py-2 text-[14px]`} />
                                </label>
                            ))}
                        </div>
                    </div>
                }

                {question.type === 'RATING' &&
                    <div className="flex flex-wrap items-center gap-3">
                        <select value={question.scale.max} onChange={(e) => patch({ scale: { min: 1, max: Number(e.target.value) } })} aria-label="별 개수" className={SMALL_SELECT}>
                            {[3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>별 {n}개</option>)}
                        </select>
                        <span className="text-[22px] leading-none tracking-wider text-amber-300" aria-hidden="true">{'★'.repeat(question.scale.max)}</span>
                    </div>
                }

                {isPage &&
                    <label className="flex flex-wrap items-center gap-2 rounded-xl bg-gray-50 px-3.5 py-2.5 text-[13px] font-semibold text-gray-600">
                        이 섹션을 마치면
                        <TargetSelect value={question.next} targets={targets} onChange={(next) => patch({ next })} nextLabel="다음 섹션으로 (마지막이면 제출)" label="이 섹션을 마치면 갈 곳" />
                    </label>
                }

                <div className="mt-1 flex flex-wrap items-center gap-1 border-t border-gray-100 pt-3">
                    <button type="button" onClick={() => onMove(index - 1)} disabled={index === 0} aria-label="위로" title="위로" className={ICON_BUTTON}>↑</button>
                    <button type="button" onClick={() => onMove(index + 1)} disabled={index === count - 1} aria-label="아래로" title="아래로" className={ICON_BUTTON}>↓</button>
                    <button type="button" onClick={onDuplicate} className={ICON_BUTTON}>복제</button>
                    <button type="button" onClick={onRemove} className={`${ICON_BUTTON} hover:text-[var(--redColor1)]`}>삭제</button>
                    {BRANCH_TYPES.includes(question.type) && hasSections &&
                        <label className="flex cursor-pointer items-center gap-2 pl-2 text-[13px] font-semibold text-gray-600">
                            답변에 따라 섹션 이동
                            <input type="checkbox" role="switch" checked={!!question.branching} onChange={(e) => patch({ branching: e.target.checked || undefined })} className="peer sr-only" />
                            <span className={SWITCH} aria-hidden="true" />
                        </label>
                    }
                    {!isBlock &&
                        <label className="ml-auto flex cursor-pointer items-center gap-2 pl-2 text-[13px] font-semibold text-gray-600">
                            필수
                            <input type="checkbox" role="switch" checked={question.required} onChange={(e) => patch({ required: e.target.checked })} className="peer sr-only" />
                            <span className={SWITCH} aria-hidden="true" />
                        </label>
                    }
                </div>
            </div>
        </div>
    );
}
